import React,{useState,useEffect, useRef} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import {format,parseISO} from 'date-fns';
import Papa from 'papaparse';
import {FiArrowRight} from 'react-icons/fi';

const {FiBookOpen,FiFilter,FiDownload,FiCalendar,FiDollarSign, FiCreditCard, FiHome}=FiIcons;


const BalanceCard = ({ title, amount, icon, color, bgColor, loading }) => (
    <div className={`p-6 rounded-lg shadow ${bgColor}`}>
        <div className="flex items-center justify-between">
            <div>
                <p className="text-sm font-medium text-grey-600">{title}</p>
                {loading ? (
                     <div className="h-8 w-24 bg-gray-200 rounded animate-pulse mt-1"></div>
                ) : (
                    <p className="text-2xl font-bold text-grey-900">QR {parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                )}
            </div>
            <div className={`p-3 rounded-full ${color} bg-opacity-20`}>
                <SafeIcon icon={icon} className={`w-6 h-6 ${color}`} />
            </div>
        </div>
    </div>
);

const Accounts=()=> {
  // State management
  const [accounts,setAccounts]=useState([]);
  const [groupedAccounts,setGroupedAccounts]=useState({});
  const [selectedAccount,setSelectedAccount]=useState('');
  const [accountName,setAccountName]=useState('');
  const [ledger,setLedger]=useState([]);
  const [loading,setLoading]=useState(true);
  const [loadingLedger,setLoadingLedger]=useState(false);
  const [dateRange,setDateRange]=useState({
    start_date: format(new Date(new Date().getFullYear(),new Date().getMonth(),1),'yyyy-MM-dd'),
    end_date: format(new Date(),'yyyy-MM-dd'),
  });
  const [balances, setBalances] = useState({ Cash: 0, Card: 0, Bank: 0 });
  const [loadingBalances, setLoadingBalances] = useState(true);
  
  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({
    from_account: 'Cash',
    to_account: 'Bank',
    amount: '',
    description: ''
  });
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState('');
  const modalRef = useRef(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 1
  });
  const [paginatedLedger, setPaginatedLedger] = useState([]);

  // Close modal when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowTransferModal(false);
      }
    }
    if (showTransferModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTransferModal]);

  // Refresh balances when component mounts and when transactions might have occurred
  useEffect(() => {
      fetchBalances();
      
      // Set up polling to refresh balances every 5 seconds
      const intervalId = setInterval(fetchBalances, 5000);
      
      // Clean up interval on component unmount
      return () => clearInterval(intervalId);
  }, []);

  const fetchBalances = async () => {
      try {
          setLoadingBalances(true);
          const response = await axios.get('/api/expenses/accounts');
          const balancesData = response.data.reduce((acc, item) => {
              acc[item.account_type] = parseFloat(item.balance);
              return acc;
          }, { Cash: 0, Card: 0, Bank: 0 });
          setBalances(balancesData);
      } catch (error) {
          console.error('Error fetching balances:', error);
          // Set default values if API fails
          setBalances({ Cash: 0, Card: 0, Bank: 0 });
      } finally {
          setLoadingBalances(false);
      }
  };

  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    setTransferData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || '' : value
    }));
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    setTransferError('');
    
    if (!transferData.amount || transferData.amount <= 0) {
      setTransferError('Please enter a valid amount');
      return;
    }
    
    if (transferData.from_account === transferData.to_account) {
      setTransferError('Cannot transfer between the same accounts');
      return;
    }
    
    try {
      setIsTransferring(true);
      const response = await axios.post('/api/expenses/accounts/transfer', {
        from_account: transferData.from_account,
        to_account: transferData.to_account,
        amount: parseFloat(transferData.amount),
        description: transferData.description || `Transfer to ${transferData.to_account}`
      });
      
      // Refresh balances and close modal
      await fetchBalances();
      setShowTransferModal(false);
      setTransferData({
        from_account: 'Cash',
        to_account: 'Bank',
        amount: '',
        description: ''
      });
      
    } catch (error) {
      console.error('Transfer failed:', error);
      setTransferError(error.response?.data?.error || 'Failed to process transfer');
    } finally {
      setIsTransferring(false);
    }
  };


  // Fetch chart of accounts on component mount
  useEffect(()=> {
    const fetchAccounts=async ()=> {
      try {
        const response=await axios.get('/api/settings/chart-of-accounts');
        setAccounts(response.data);
        const grouped=response.data.reduce((acc,account)=> {
          const {category}=account;
          if (!acc[category]) acc[category]=[];
          acc[category].push(account);
          return acc;
        },{});
        setGroupedAccounts(grouped);
        if (response.data.length > 0) {
          setSelectedAccount(response.data[0].id);
          setAccountName(`${response.data[0].code} - ${response.data[0].name}`);
        }
      } catch (error) {
        console.error('Error fetching accounts:',error);
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  },[]);

  // Fetch ledger when account or date range changes
  useEffect(() => {
    if (selectedAccount) {
      fetchLedger();
    }
    // Also refresh balances when these change to ensure they're up to date
    fetchBalances();
  },[selectedAccount, dateRange]);

  const fetchLedger=async ()=> {
    setLoadingLedger(true);
    try {
      const params=new URLSearchParams({
        account_id: selectedAccount,
        ...dateRange,
      });
      const response=await axios.get(`/api/reports/account-ledger?${params}`);
      
      let balance=0;
      const ledgerWithBalance = response.data.map(tx => {
        balance += (tx.debit || 0) - (tx.credit || 0);
        return { ...tx, balance };
      });
      setLedger(ledgerWithBalance);
      
      // Update pagination
      const totalItems = ledgerWithBalance.length;
      const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);
      setPagination(prev => ({
        ...prev,
        totalItems,
        totalPages,
        currentPage: 1 // Reset to first page when data changes
      }));
      
      // Update paginated data
      updatePaginatedData(ledgerWithBalance);
    } catch (error) {
      console.error('Error fetching ledger:',error);
      setLedger([]);
    } finally {
      setLoadingLedger(false);
    }
  };

  const handleAccountChange = (e) => {
    const accountId = e.target.value;
    setSelectedAccount(accountId);
    const selected = accounts.find(acc => acc.id.toString() === accountId);
    if (selected) {
      setAccountName(`${selected.code} - ${selected.name}`);
    }
  };

  const updatePaginatedData = (data = ledger) => {
    const { currentPage, itemsPerPage } = pagination;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedLedger(data.slice(startIndex, endIndex));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        currentPage: newPage
      }));
    }
  };

  // Update paginated data when pagination or ledger changes
  useEffect(() => {
    if (ledger.length > 0) {
      updatePaginatedData();
    }
  }, [pagination.currentPage, ledger]);
  
  const handleExport=()=> {
    const dataToExport=ledger.map(item=> ({
      Date: format(parseISO(item.date),'yyyy-MM-dd'),
      Description: item.description,
      Vendor: item.vendor || 'N/A',
      'Payment Method': item.payment_method,
      Debit: item.debit ? parseFloat(item.debit).toFixed(2) : '',
      Credit: item.credit ? parseFloat(item.credit).toFixed(2) : '',
      Balance: parseFloat(item.balance).toFixed(2),
    }));

    const csv=Papa.unparse(dataToExport);
    const blob=new Blob([csv],{type: 'text/csv;charset=utf-8;'});
    const link=document.createElement('a');
    const url=URL.createObjectURL(blob);
    link.setAttribute('href',url);
    const safeAccountName=accountName.replace(/[^a-z0-9]/gi,'_').toLowerCase();
    link.setAttribute('download',`ledger_${safeAccountName}_${dateRange.start_date}_to_${dateRange.end_date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="text-center p-8">Loading accounts...</div>;
  }

  return (
    <div>
    <div className="space-y-6 mb-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Accounts Overview</h1>
        <button
          onClick={() => setShowTransferModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
        >
          <FiArrowRight className="-ml-1 mr-2 h-5 w-5" />
          Transfer Between Accounts
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <BalanceCard
            title="Cash Balance"
            amount={balances.Cash}
            icon={FiDollarSign}
            color="text-green-800"
            bgColor="bg-green-100/60"
            loading={loadingBalances}
        />
        <BalanceCard
            title="Card Balance"
            amount={balances.Card}
            icon={FiCreditCard}
            color="text-blue-800"
            bgColor="bg-blue-100/60"
            loading={loadingBalances}
        />
        <BalanceCard
            title="Bank Balance"
            amount={balances.Bank}
            icon={FiHome}
            color="text-purple-800"
            bgColor="bg-purple-100/60"
            loading={loadingBalances}
        />
      </div>
    </div> 
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-grey-900">Account Ledger</h1>
        
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-grey-700 mb-1">Account</label>
            <select
              className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
              value={selectedAccount}
              onChange={handleAccountChange}
            >
              {Object.entries(groupedAccounts).map(([category,accs])=> (
                <optgroup label={category} key={category}>
                  {accs.map(account=> (
                    <option key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">Start Date</label>
            <input type="date" className="w-full px-3 py-2 border border-grey-300 rounded-lg" value={dateRange.start_date} onChange={e=> setDateRange({...dateRange,start_date: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">End Date</label>
            <input type="date" className="w-full px-3 py-2 border border-grey-300 rounded-lg" value={dateRange.end_date} onChange={e=> setDateRange({...dateRange,end_date: e.target.value})} />
          </div>
        </div>
      </div>
      
      {/* Ledger Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-grey-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-grey-900">Ledger: {accountName}</h3>
            <p className="text-sm text-grey-600">
              {format(parseISO(dateRange.start_date),'MMM dd,yyyy')} - {format(parseISO(dateRange.end_date),'MMM dd,yyyy')}
            </p>
          </div>
          <div className="flex space-x-2">
            
            <button
              onClick={handleExport}
              disabled={ledger.length===0}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:bg-grey-300"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-grey-200">
            <thead className="bg-grey-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-grey-500 uppercase">Debit</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-grey-500 uppercase">Credit</th>
                
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingLedger ? (
                <tr><td colSpan="6" className="text-center p-8">Loading ledger...</td></tr>
              ) : paginatedLedger.length===0 ? (
                <tr><td colSpan="6" className="text-center p-8 text-grey-500">No transactions found for this period.</td></tr>
              ) : (
                paginatedLedger.map((item,index)=> (
                  <motion.tr key={index} initial={{opacity: 0}} animate={{opacity: 1}}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{format(parseISO(item.date),'MMM dd,yyyy')}</td>
                    <td className="px-6 py-4 text-sm">{item.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{item.vendor || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                      {item.debit ? `QR ${parseFloat(item.debit).toLocaleString(undefined,{minimumFractionDigits: 2,maximumFractionDigits: 2})}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                      {item.credit ? `QR ${parseFloat(item.credit).toLocaleString(undefined,{minimumFractionDigits: 2,maximumFractionDigits: 2})}` : '-'}
                    </td>
                    
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">
                    {Math.min((pagination.currentPage - 1) * pagination.itemsPerPage + 1, pagination.totalItems)}
                  </span> to <span className="font-medium">
                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                  </span> of <span className="font-medium">{pagination.totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="First page"
                  >
                    <span className="sr-only">First</span>
                    <SafeIcon icon={FiIcons.FiChevronsLeft} className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous page"
                  >
                    <span className="sr-only">Previous</span>
                    <SafeIcon icon={FiIcons.FiChevronLeft} className="h-5 w-5" />
                  </button>
                  <div className="px-4 py-2 text-sm text-gray-700">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next page"
                  >
                    <span className="sr-only">Next</span>
                    <SafeIcon icon={FiIcons.FiChevronRight} className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Last page"
                  >
                    <span className="sr-only">Last</span>
                    <SafeIcon icon={FiIcons.FiChevronsRight} className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Transfer Modal */}
    {showTransferModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Transfer Between Accounts</h3>
          </div>
          
          <form onSubmit={handleTransferSubmit} className="p-6 space-y-4">
            {transferError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{transferError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label htmlFor="from_account" className="block text-sm font-medium text-gray-700">From Account</label>
              <select
                id="from_account"
                name="from_account"
                value={transferData.from_account}
                onChange={handleTransferChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm rounded-md"
              >
                {['Cash', 'Card', 'Bank'].map(account => (
                  <option key={`from-${account}`} value={account}>
                    {account} (QR {balances[account]?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-center py-2">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <FiArrowRight className="h-4 w-4 text-gray-500" />
              </div>
            </div>
            
            <div>
              <label htmlFor="to_account" className="block text-sm font-medium text-gray-700">To Account</label>
              <select
                id="to_account"
                name="to_account"
                value={transferData.to_account}
                onChange={handleTransferChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm rounded-md"
              >
                {['Cash', 'Card', 'Bank']
                  .filter(account => account !== transferData.from_account)
                  .map(account => (
                    <option key={`to-${account}`} value={account}>
                      {account} (QR {balances[account]?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'})
                    </option>
                  ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount (QR)</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={transferData.amount}
                onChange={handleTransferChange}
                min="0.01"
                step="0.01"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                placeholder="0.00"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Available: QR {balances[transferData.from_account]?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
              </p>
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (Optional)</label>
              <input
                type="text"
                id="description"
                name="description"
                value={transferData.description}
                onChange={handleTransferChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                placeholder="e.g., Transfer to main account"
              />
            </div>
            
            <div className="pt-2 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
                disabled={isTransferring}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 disabled:opacity-50"
                disabled={isTransferring || !transferData.amount}
              >
                {isTransferring ? 'Transferring...' : 'Transfer Funds'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
  </div>
  );
};

export default Accounts;